<template>
  <div class="app-version-management-wrapper">
    <v-card
      :loading="loading"
      class="fill-width table_theme card-theme app-version-custom-container"
    >
      <v-card-title class="title_table medium-font" style="letter-spacing: normal">
        App Version Management
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
          style="padding: 0px 30px;"
        ></v-progress-circular>
      </v-card-title>
      <v-divider class="ma-0" style="max-width: 1096px; margin-left: 13px !important;"></v-divider>

      <v-card-text class="pt-4">
        <p class="mb-4" style="color: #666; font-size: 14px;">
          Manage iOS and Android update thresholds. The <strong>build</strong> number triggers a soft
          update prompt; the <strong>min_build</strong> number triggers a forced update block.
        </p>

        <v-row v-if="!loading">
          <!-- iOS Panel -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <v-card-title class="px-0 pt-0 pb-2" style="font-size: 18px; color: #061D47;">
                <v-icon left color="#061D47">mdi-apple</v-icon>
                iOS
              </v-card-title>
              <v-divider class="mb-4"></v-divider>

              <div v-for="env in environments" :key="'ios-' + env">
                <div class="env-section-label mb-2">{{ capitalize(env) }}</div>
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="ios[env].version"
                      label="Version"
                      outlined
                      dense
                      placeholder="1.0.0"
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="ios[env].build"
                      label="Build (soft)"
                      outlined
                      dense
                      placeholder="100"
                      hide-details="auto"
                      :error-messages="buildErrors.ios[env]"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="ios[env].min_build"
                      label="Min Build (forced)"
                      outlined
                      dense
                      placeholder="90"
                      hide-details="auto"
                      :error-messages="minBuildErrors.ios[env]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-divider v-if="env !== 'release'" class="my-4"></v-divider>
              </div>

              <div class="mt-4 d-flex justify-end">
                <v-btn
                  color="primary"
                  :loading="savingIos"
                  :disabled="savingIos"
                  @click="savePlatform('ios')"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Save iOS
                </v-btn>
              </div>
            </v-card>
          </v-col>

          <!-- Android Panel -->
          <v-col cols="12" md="6">
            <v-card outlined class="pa-4">
              <v-card-title class="px-0 pt-0 pb-2" style="font-size: 18px; color: #061D47;">
                <v-icon left color="#061D47">mdi-android</v-icon>
                Android
              </v-card-title>
              <v-divider class="mb-4"></v-divider>

              <div v-for="env in environments" :key="'android-' + env">
                <div class="env-section-label mb-2">{{ capitalize(env) }}</div>
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="android[env].version"
                      label="Version"
                      outlined
                      dense
                      placeholder="1.0.0"
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="android[env].build"
                      label="Build (soft)"
                      outlined
                      dense
                      placeholder="100"
                      hide-details="auto"
                      :error-messages="buildErrors.android[env]"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="android[env].min_build"
                      label="Min Build (forced)"
                      outlined
                      dense
                      placeholder="90"
                      hide-details="auto"
                      :error-messages="minBuildErrors.android[env]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-divider v-if="env !== 'release'" class="my-4"></v-divider>
              </div>

              <div class="mt-4 d-flex justify-end">
                <v-btn
                  color="primary"
                  :loading="savingAndroid"
                  :disabled="savingAndroid"
                  @click="savePlatform('android')"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Save Android
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Loading skeleton -->
        <v-row v-else>
          <v-col cols="12" md="6">
            <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" md="6">
            <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import { firestore, auth } from '@/config/firebase';
import config from '@/config/constants';
import { ensurePassphrase } from '@/util/super_admin_passphrase';

const PLATFORMS = ['ios', 'android'];
const ENVIRONMENTS = ['beta', 'staging', 'release'];

function emptyEnvData() {
  return {
    version: '',
    build: '',
    min_build: '',
  };
}

function emptyPlatformData() {
  return {
    beta: emptyEnvData(),
    staging: emptyEnvData(),
    release: emptyEnvData(),
  };
}

function emptyErrors() {
  return {
    ios: { beta: '', staging: '', release: '' },
    android: { beta: '', staging: '', release: '' },
  };
}

export default {
  name: 'AppVersionManagement',

  data() {
    return {
      loading: true,
      savingIos: false,
      savingAndroid: false,
      environments: ENVIRONMENTS,
      ios: emptyPlatformData(),
      android: emptyPlatformData(),
      buildErrors: emptyErrors(),
      minBuildErrors: emptyErrors(),
      snackbar: {
        show: false,
        message: '',
        color: 'success',
      },
    };
  },

  mounted() {
    this.fetchVersionData();
  },

  methods: {
    async fetchVersionData() {
      this.loading = true;
      try {
        const [iosDoc, androidDoc] = await Promise.all(
          PLATFORMS.map((platform) =>
            firestore.collection('app_current_version').doc(platform).get()
          )
        );

        if (iosDoc.exists) {
          this.populatePlatformData('ios', iosDoc.data());
        }

        if (androidDoc.exists) {
          this.populatePlatformData('android', androidDoc.data());
        }
      } catch (error) {
        console.error('Error fetching version data:', error);
        this.showSnackbar('Error loading version data. Please try again.', 'error');
      } finally {
        this.loading = false;
      }
    },

    populatePlatformData(platform, docData) {
      ENVIRONMENTS.forEach((env) => {
        if (docData[env]) {
          this[platform][env] = {
            version: docData[env].version || '',
            build: docData[env].build || '',
            min_build: docData[env].min_build || '',
          };
        }
      });
    },

    validatePlatform(platform) {
      let valid = true;

      ENVIRONMENTS.forEach((env) => {
        const buildVal = this[platform][env].build;
        const minBuildVal = this[platform][env].min_build;

        const buildIsNumeric = buildVal === '' || /^\d+$/.test(buildVal);
        const minBuildIsNumeric = minBuildVal === '' || /^\d+$/.test(minBuildVal);

        this.buildErrors[platform][env] = buildIsNumeric ? '' : 'Must be a numeric value';
        this.minBuildErrors[platform][env] = minBuildIsNumeric ? '' : 'Must be a numeric value';

        if (!buildIsNumeric || !minBuildIsNumeric) {
          valid = false;
        }
      });

      return valid;
    },

    async savePlatform(platform) {
      if (!this.validatePlatform(platform)) {
        this.showSnackbar('Please fix validation errors before saving.', 'error');
        return;
      }

      // Destructive op (forces app upgrades) — prompt for the
      // super-admin passphrase up-front while we still have a user
      // gesture. The axios interceptor would otherwise try to prompt
      // mid-request and Chrome blocks that as a non-user-gesture.
      const passphrase = ensurePassphrase();
      if (!passphrase) {
        this.showSnackbar('Super-admin passphrase required to push app version updates.', 'error');
        return;
      }

      const savingKey = platform === 'ios' ? 'savingIos' : 'savingAndroid';
      this[savingKey] = true;

      try {
        const payload = {};
        ENVIRONMENTS.forEach((env) => {
          payload[env] = {
            version: this[platform][env].version,
            build: this[platform][env].build,
            min_build: this[platform][env].min_build,
          };
        });

        // Use Firebase ID token (works pre-login and post-login)
        const firebaseUser = auth.currentUser;
        const headers = {};
        if (firebaseUser) {
          const idToken = await firebaseUser.getIdToken();
          headers['X-Firebase-Token'] = idToken;
        }

        await axios.put(config.api_url + 'app-version/' + platform, payload, { headers });

        const platformLabel = platform === 'ios' ? 'iOS' : 'Android';
        this.showSnackbar(`${platformLabel} version data saved successfully.`, 'success');
      } catch (error) {
        console.error(`Error saving ${platform} version data:`, error);
        const status = error && error.response && error.response.status;
        const reason = error && error.response && error.response.data && error.response.data.errors;
        if (status === 403) {
          const reasonText = Array.isArray(reason) ? reason.join(', ') : String(reason || '');
          if (reasonText.includes('destructive_passphrase_required')) {
            this.showSnackbar('Super-admin passphrase rejected — click Save again to re-enter.', 'error');
          } else if (reasonText.includes('super_user required')) {
            this.showSnackbar('Super-admin access required.', 'error');
          } else {
            this.showSnackbar(`Access denied: ${reasonText || '403'}`, 'error');
          }
        } else {
          this.showSnackbar(`Error saving ${platform} data. Please try again.`, 'error');
        }
      } finally {
        this[savingKey] = false;
      }
    },

    showSnackbar(message, color) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
  },
};
</script>

<style scoped>
.app-version-management-wrapper {
  padding: 24px;
}

.app-version-custom-container {
  max-width: 1222px !important;
  margin: 0 auto;
  -webkit-box-shadow: 0 20px 15px -15px rgba(0, 0, 0, 0.1) !important;
  -moz-box-shadow: 0 20px 15px -15px rgba(0, 0, 0, 0.1) !important;
  box-shadow: 0 20px 15px -15px rgba(0, 0, 0, 0.1) !important;
}

.env-section-label {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
}
</style>
